import React, { useCallback, useEffect, useMemo } from 'react';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { io, ManagerOptions, SocketOptions } from 'socket.io-client';

import { fetchAllUsersSuccessful } from './components/admin/users/store/actions';
import { ClientUser } from './components/admin/users/store/types';
import { idleCheckToken, logoutStarted, redirectToLogin, setSocketsConnection, setSocketsConnectionFailed, socketLogin, systemMaintenanceIdsUpdated, toggleLogoutWarningModal, userUpdated } from './components/auth/login/store/actions';
import { notificationsReceived, setActiveMLDocuments, setActiveMLOpinions, setPortfolioStatistics, triggerProgressActiveFewShotDocuments, triggerProgressActiveMLDocuments, triggerProgressActiveMLOpinions, triggerProgressTestMLDocuments } from './components/auth/notifications/store/actions';
import { ActiveFewShotDocument, ActiveMLDocument, Notification, TestMLDocument } from './components/auth/notifications/store/types';
import { documentAnalysisComplete, documentAnalysisFailed, documentNettingAnalysisComplete } from './components/documents/my-documents/store/actions';
import { useAppDispatch, useAppSelector } from './hooks/react-redux';
import { apiRoot } from './services/services';
import { setUpdatedOpinionSummaryId } from './components/opinions/my-opinions/store/actions';
import { ecsTableExtractionComplete, openDatasetInstanceById } from './components/datasets/instances/store/actions';
import { setRiskToleranceCompleted } from './components/documents/analytics/store/actions';
import { Continent, Country } from './components/documents/analytics/store/types';
import { setContinentGeometryJSON, setWorldGeometryJSON } from './components/home/store/actions';
import { setAllCalculatorFields } from './components/admin/calculator/store/actions';
import { CalculatorFieldTypeDB, CalculatorPadOptionDB } from './components/admin/calculator/store/types';
import { ActiveMLOpinion } from '../../services/types/ML';
import { downloadClientData } from './components/admin/clients/store/actions';
import { setDatasetRiskToleranceIsRecalculating } from './components/admin/client-risk-tolerance/store/actions';
import { DocumentNettingAnalysis } from './components/documents/store/types';
import { User } from './components/auth/login/store/types';
import { getSocket, getIsLoggedIn } from './components/auth/login/store';
import { documentNettingDetailsAutoUpdateCompleted } from './components/opinions/netting-engine/store/actions';
import { DocumentNettingAutoUpdate } from './components/opinions/netting-engine/store/types';

export const Sockets: React.FC = () => {
    const dispatch = useAppDispatch();
    const socket = useAppSelector(getSocket);
    const isLoggedIn = useAppSelector(getIsLoggedIn);

    const onIdle = useCallback((event?: Event | undefined, idleTimer?: IIdleTimer | undefined) => {
        if (isLoggedIn && socket) {
            dispatch(idleCheckToken());
            idleTimer?.reset();
        }
    }, [isLoggedIn, socket, dispatch]);

    // Run a timer which checks if user is idle every hour, this triggers a token expiry check and if token is not valid they will be logged out
    useIdleTimer({ onIdle, timeout: 1000 * 60 * 60 });

    useEffect(() => {
        if (socket) {
            /* eslint-disable no-console */
            socket.on('connect', () => console.log('Connected...'));
            socket.on('token-nearing-expiry', () => dispatch(toggleLogoutWarningModal(true)));
            socket.on('token-extended', () => dispatch(toggleLogoutWarningModal(false)));
            socket.on('token-expired', () => dispatch(logoutStarted()));
            socket.on('password-updated', () => dispatch(logoutStarted()));
            socket.on('user-deleted', () => dispatch(logoutStarted()));
            socket.on('token-valid', (user: User) => dispatch(socketLogin(user)));
            socket.on('token-invalid', () => dispatch(redirectToLogin()));
            socket.on('user-updated', (user: User) => dispatch(userUpdated(user)));
            socket.on('document-analysis-complete', (documentId: number) => dispatch(documentAnalysisComplete(documentId)));
            socket.on('document-analysis-failed', (documentId: number) => dispatch(documentAnalysisFailed(documentId)));
            socket.on('notifications', (notifications: Notification[]) => dispatch(notificationsReceived(notifications)));
            socket.on('all-users', (allUsers: ClientUser[]) => dispatch(fetchAllUsersSuccessful(allUsers)));
            socket.on('opinion-summary-complete', ({ opinionId, docxOpinionSummaryId, pdfOpinionSummaryId }: { opinionId: number; pdfOpinionSummaryId: number; docxOpinionSummaryId: number; }) => dispatch(setUpdatedOpinionSummaryId(opinionId, pdfOpinionSummaryId, docxOpinionSummaryId)));
            socket.on('ml-progress', (activeMLDocument: ActiveMLDocument) => dispatch(triggerProgressActiveMLDocuments(activeMLDocument, true)));
            socket.on('opinion-ml-progress', (activeMLOpinion: ActiveMLOpinion) => dispatch(triggerProgressActiveMLOpinions(activeMLOpinion, true)));
            socket.on('few-shot-document-ml-progress', (activeFewShotDocument: ActiveFewShotDocument) => dispatch(triggerProgressActiveFewShotDocuments(activeFewShotDocument, true)));
            socket.on('active-ml-opinions', (activeMLOpinions: ActiveMLOpinion[]) => dispatch(setActiveMLOpinions(activeMLOpinions)));
            socket.on('active-ml-documents', (activeMLDocuments: ActiveMLDocument[]) => dispatch(setActiveMLDocuments(activeMLDocuments)));
            socket.on('portfolio-statistics', (statistics: string[]) => dispatch(setPortfolioStatistics(statistics)));
            socket.on('agency-amendment-complete', ({ originalDocumentId, datasetInstanceId }: { originalDocumentId: number; datasetInstanceId: string; }) => dispatch(openDatasetInstanceById(datasetInstanceId, false, originalDocumentId, true)));
            socket.on('user-logout', () => dispatch(redirectToLogin()));
            socket.on('document-risk-completed', () => dispatch(setRiskToleranceCompleted()));
            socket.on('document-dataset-risk-completed', (datasetId: number) => dispatch(setDatasetRiskToleranceIsRecalculating(datasetId, false)));
            socket.on('world-geometry', (worldGeometry: Country[]) => dispatch(setWorldGeometryJSON(worldGeometry)));
            socket.on('continent-geometry', (continentGeometry: Continent[]) => dispatch(setContinentGeometryJSON(continentGeometry)));
            socket.on('calculator-fields', ({ calculatorFields, padOptions }: { calculatorFields: CalculatorFieldTypeDB[]; padOptions: CalculatorPadOptionDB[]; }) => dispatch(setAllCalculatorFields(calculatorFields, padOptions)));
            socket.on('test-document-ml-progress', (testMLDocument: TestMLDocument) => dispatch(triggerProgressTestMLDocuments(testMLDocument, true)));
            socket.on('all-client-data', ({ workbook, clientName }: { workbook: Buffer; clientName: string; }) => dispatch(downloadClientData(workbook, clientName)));
            socket.on('ecs-table-populated', ({ documentId, instanceId }: { documentId: number; instanceId: string | null; }) => dispatch(ecsTableExtractionComplete(documentId, instanceId)));
            socket.on('netting-analysis-complete', (documentNettingAnalysis: DocumentNettingAnalysis) => dispatch(documentNettingAnalysisComplete(documentNettingAnalysis)));
            socket.on('netting-auto-update-complete', (autoUpdate: DocumentNettingAutoUpdate) => dispatch(documentNettingDetailsAutoUpdateCompleted(autoUpdate)));
            socket.on('system-maintenance-ids-updated', (systemMaintenanceIds: number[]) => dispatch(systemMaintenanceIdsUpdated(systemMaintenanceIds)));
        }
    }, [socket, dispatch]);

    const options: Partial<SocketOptions & ManagerOptions> = useMemo(() => ({
        auth: {},
        transports: ['websocket'],
        withCredentials: true,
        secure: true,
        rejectUnauthorized: false,
        port: '80'
    }), []);

    useEffect(() => {
        try {
            const socketConnection = io(apiRoot()!, options);
            dispatch(setSocketsConnection(socketConnection));
        } catch (err) {
            const error = err as Error;
            dispatch(setSocketsConnectionFailed(error.message));
        }
    }, [options, dispatch]);

    return <div />;
};
